import {
  Button,
  Col,
  Input,
  message,
  Modal,
  Row,
  Space,
  Upload,
  UploadFile,
} from 'antd';
import { useEffect, useState } from 'react';
import { UploadOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import {
  CompanyMediaAddItemDTO,
  companyMediaController,
  CompanyMediaTypeEnum,
  oSSController,
} from '../../api';
import { useCompany } from '../../hooks/company';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

const renderKey = async (file: UploadFile) => {
  const now = dayjs();
  const array = new Uint32Array(128);
  window.crypto.getRandomValues(array);
  const randText = array.join('');
  const message = `${now.format('YYYYMMDD')}/${randText}${file.name}${
    file.uid
  }${+new Date()}`;

  const msgUint8 = new TextEncoder().encode(message); // encode as (utf-8) Uint8Array
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8); // hash the message
  const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join(''); // convert bytes to hex string
  return hashHex;
};

export default () => {
  const [uploadModalVisible, setUploadModalVisible] = useState(false);

  const { id: companyId, hasPermission } = useCompany();
  const [uploadLoading, setUploadLoading] = useState<boolean>();

  const [waitAddItem, setWaitAddItem] = useState<CompanyMediaAddItemDTO[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const [category, setCategory] = useState<string>();

  useEffect(() => {
    setUploadLoading(false);
    setCategory(undefined);
    setWaitAddItem([]);
    setFileList([]);
  }, [uploadModalVisible, setUploadLoading, setCategory, setWaitAddItem]);

  const { runAsync: addItems, loading: addLoading } = useRequest(
    () => {
      return companyMediaController.addMedias(companyId, {
        category,
        items: waitAddItem,
      });
    },
    {
      manual: true,
      onSuccess: () => {
        message.success('上传成功');
        setWaitAddItem([]);
        setUploadModalVisible(false);
        setUploadLoading(undefined);
      },
    },
  );

  const { data: uploadInfo, loading: uploadInfoLoading } = useRequest(
    () => {
      return oSSController.getOSSMediaUploadToken(companyId);
    },
    {
      refreshDeps: [open, companyId],
    },
  );

  if (!hasPermission('page:media:editor')) {
    return null;
  }
  const loading = uploadInfoLoading || addLoading || uploadLoading;
  // 上传组件
  return (
    <>
      <Modal
        confirmLoading={loading}
        onCancel={() => setUploadModalVisible(false)}
        open={uploadModalVisible}
        footer={[
          <Button key="cancel" onClick={() => setUploadModalVisible(false)}>
            取消
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={uploadLoading}
            disabled={!category || !waitAddItem.length}
            onClick={() => {
              console.log('add', waitAddItem);
              return addItems();
            }}
          >
            确定
          </Button>,
        ]}
      >
        <Row>
          <Col span={24} style={{ marginBottom: '20px' }}>
            <Space>
              <span>合集名称</span>
              <Input
                value={category}
                onChange={(v) => setCategory(v.target.value)}
              />
            </Space>
          </Col>
          <Col span={24}>
            <Upload
              directory
              fileList={fileList}
              action={uploadInfo?.host}
              accept="image/*,video/*"
              beforeUpload={(file) => {
                const isImage = file.type.startsWith('image/');
                const isVideo = file.type.startsWith('video/');
                if (isImage || isVideo) {
                  if (isImage && file.size > 1024 * 1024 * 5) {
                    message.warning(`${file.name} 超过 5M，将不会被上传`);
                    return false;
                  }
                  if (isVideo && file.size > 1024 * 1024 * 100) {
                    message.warning(`${file.name} 超过 100M，将不会被上传`);
                    return false;
                  }
                  return true;
                }
                return Upload.LIST_IGNORE;
              }}
              onChange={(info) => {
                console.log(info);
                setFileList(info.fileList);
                setUploadLoading(true);
                if (info.file.status === 'done') {
                  setUploadLoading(undefined);
                  setWaitAddItem((prev) => {
                    return [
                      ...prev,
                      {
                        name: info.file.name,
                        key: (info.file as any).oss_key,
                        size: info.file.size || 0,
                        type: info.file.type?.startsWith('video')
                          ? CompanyMediaTypeEnum.VIDEO
                          : CompanyMediaTypeEnum.PHOTO,
                      },
                    ];
                  });
                } else if (info.file.status === 'removed') {
                  setUploadLoading(undefined);
                }
              }}
              data={async (file) => {
                const ext = file.name.split('.').pop();
                const key = `${uploadInfo?.dir}${dayjs().format(
                  'YYYYMMDD',
                )}/${await renderKey(file)}${ext ? `.${ext}` : ''}`;
                // setUploadData((prev) => ({
                //   ...prev,
                //   ossKey: key,
                // }));
                (file as any).oss_key = key;
                return {
                  key,
                  OSSAccessKeyId: uploadInfo?.accessKey,
                  policy: uploadInfo?.policy,
                  signature: uploadInfo?.signature,
                };
              }}
            >
              <Button icon={<UploadOutlined />}>选择文件夹</Button>
            </Upload>
          </Col>
        </Row>
      </Modal>
      <Button
        loading={loading}
        type="primary"
        onClick={() => setUploadModalVisible(true)}
      >
        上传
      </Button>
    </>
  );
};
